/* While the site menu or search is open, the page behind it is inert, so the
   keyboard can't wander into links hidden under them. The header, the menu,
   the search and Ghost's popups (Portal) stay live */
function psyPageInert(inert) {
    const keep = '#gh-navigation, #psy-menu, #psy-search, #ghost-portal-root, script, style, link';
    document.querySelectorAll('body > *, .gh-viewport > *').forEach(function (node) {
        if (node.matches(keep) || node.querySelector('#gh-navigation, #psy-menu, #psy-search')) return;
        node.inert = inert;
    });
}

/* aria-expanded on a toggle button, and its name to match where the template
   gives one (data-label-expanded / data-label-collapsed): an icon button that
   shows ✕ while open is announced as closing, not opening */
function psySetExpanded(button, expanded) {
    button.setAttribute('aria-expanded', String(expanded));
    const label = expanded ? button.dataset.labelExpanded : button.dataset.labelCollapsed;
    if (label) button.setAttribute('aria-label', label);
}

/* Portal (Ghost's sign-up, sign-in and account popups) is 2.7 MB of script, so it
   loads when it's wanted instead of with every page (default.hbs keeps its settings
   in the inert #psy-portal tag):
   - a pointer, focus or key on anything Portal handles: [data-portal], links to
     #/portal…, data-members-* forms and buttons
   - a #/portal… hash, on arrival or set later (links, the subscribe forms, comments)
   - the query strings Portal answers: ?action=signin&success=true from a sign-in
     link, ?stripe=success after checkout, unsubscribe and gift links
   - for signed-in members, once the page is idle (their Account and billing links)
   Nothing loads it for other readers who don't touch it: everything Portal does
   for them starts from one of the above (Ghost's signup cards, which Portal would
   reveal, screen.css shows instead).
   A click or submit made before Portal is listening isn't lost: a click becomes
   the matching #/portal/… hash, which Portal opens as it starts, and a form or
   billing button goes again once Portal is ready. psyLoadPortal() returns a
   promise for that moment */
const psyLoadPortal = (function () {
    const tag = document.getElementById('psy-portal');
    if (!tag) return function () { return Promise.resolve(); };

    const handled = '[data-portal], a[href*="#/portal"], a[href*="#/share"], form[data-members-form], [data-portal-signup-form], [data-members-plan], [data-members-signout], [data-members-edit-billing], [data-members-manage-billing], [data-members-cancel-subscription], [data-members-continue-subscription]';
    // Buttons Portal binds only once it has started (forms are handled below)
    const buttons = '[data-members-plan], [data-members-signout], [data-members-edit-billing], [data-members-manage-billing], [data-members-cancel-subscription], [data-members-continue-subscription]';
    const hash = /^#\/(portal|share|feedback)\b/;
    const replay = [];
    let loading, ready = false, ran = false, late = false;

    function addScript(src, attributes) {
        return new Promise(function (resolve, reject) {
            const script = document.createElement('script');
            Object.entries(attributes || {}).forEach(([name, value]) => script.setAttribute(name, value));
            script.src = src;
            script.onload = resolve;
            script.onerror = function () {
                script.remove();
                reject(new Error('Could not load ' + src));
            };
            document.head.appendChild(script);
        });
    }

    function load() {
        if (loading) return loading;
        loading = new Promise(function (resolve) {
            function start() {
                if (ready) return;
                ready = true;
                resolve();
                // A hash set after Portal read the address bar but before it listened
                if (late && hash.test(location.hash)) window.dispatchEvent(new HashChangeEvent('hashchange'));
                replay.splice(0).forEach(go => go());
            }

            // The last thing Portal does as it starts is turn same-site links to
            // #/portal… into bare hashes, so this hidden one says it's listening
            const probe = document.createElement('a');
            probe.setAttribute('href', '/#/portal');
            probe.hidden = true;
            document.body.appendChild(probe);
            new MutationObserver(function (records, observer) {
                observer.disconnect();
                probe.remove();
                start();
            }).observe(probe, {attributes: true, attributeFilter: ['href']});

            // The attributes Ghost gives Portal's own tag
            const attributes = {crossorigin: 'anonymous'};
            Array.from(tag.attributes).forEach(function (attribute) {
                if (/^data-/.test(attribute.name) && attribute.name !== 'data-src') attributes[attribute.name] = attribute.value;
            });
            addScript(tag.dataset.src, attributes).then(function () {
                ran = true;
                // In case a future Portal stops rewriting links
                setTimeout(start, 10000);
            }, function () {
                // Blocked or offline: the next try loads it again
                probe.remove();
                loading = null;
            });

            // Checkout is a Stripe-hosted page, so Portal only calls Stripe.js
            // (window.Stripe(key).redirectToCheckout) to update a paid member's
            // card. ghost_head leaves it out, so it's fetched then
            if (!window.Stripe && !document.querySelector('script[src^="https://js.stripe.com"]')) {
                window.Stripe = function (key) {
                    return {
                        redirectToCheckout: function (options) {
                            delete window.Stripe;
                            return addScript('https://js.stripe.com/v3/')
                                .then(() => window.Stripe(key).redirectToCheckout(options));
                        }
                    };
                };
            }
        });
        return loading;
    }

    // Start fetching as soon as a reader reaches for something Portal handles
    ['pointerdown', 'focusin', 'keydown'].forEach(function (type) {
        document.addEventListener(type, function (event) {
            if (!ready && event.target.closest && event.target.closest(handled)) load();
        }, {capture: true, passive: true});
    });

    document.addEventListener('click', function (event) {
        if (ready || !event.target.closest) return;
        const trigger = event.target.closest(handled);
        if (!trigger) return;
        load();
        if (trigger.matches('[data-portal]')) {
            // Portal ignores clicks until it has started, but reads the hash then
            event.preventDefault();
            if (ran) late = true;
            location.hash = '#/portal/' + trigger.dataset.portal.replace(/^\//, '');
        } else if (trigger.matches(buttons)) {
            event.preventDefault();
            replay.push(() => trigger.click());
        }
        // Links to #/portal… set the hash themselves
    });

    // A members form sent before Portal is listening (Portal would have stopped
    // the browser's own submit) is sent again, the way Portal sees it, once it is
    document.addEventListener('submit', function (event) {
        const form = event.target;
        if (ready || event.defaultPrevented || !form.matches('form[data-members-form]')) return;
        event.preventDefault();
        form.classList.add('loading');
        replay.push(() => form.dispatchEvent(new Event('submit', {bubbles: true, cancelable: true})));
        load();
    });

    window.addEventListener('hashchange', function () {
        if (!hash.test(location.hash)) return;
        if (ran && !ready) late = true;
        load();
    });

    if (hash.test(location.hash) || /[?&](action|success|stripe|giftRedemption|gift_token|token)=/.test(location.search)) {
        load();
    } else if (document.body.classList.contains('is-member')) {
        window.addEventListener('load', function () {
            if (window.requestIdleCallback) requestIdleCallback(load, {timeout: 4000});
            else setTimeout(load, 2000);
        });
    }

    return load;
})();

/* Burger layout, used whenever the header doesn't fit on one line. The burger
   (brand row) and the Menu button (wide screens) open and close the site menu
   (partials/components/menu.hbs), which covers the window, header included */
(function () {
    const navigation = document.querySelector('.gh-navigation');
    const burgers = navigation.querySelectorAll('.gh-burger, .gh-menu-button');
    if (!burgers.length) return;

    const inner = navigation.querySelector('.gh-navigation-inner');
    const dialog = document.getElementById('psy-menu');

    // Measure the header laid out on one line at its natural width; if that's
    // wider than the space available, fall back to the burger
    function fitMenu() {
        let compact = window.innerWidth < 768;
        // Measure at the final sizes: the logo and tagline sizes are animated,
        // so leaving the burger layout would otherwise measure them mid-change
        navigation.style.transition = 'none';

        if (!compact) {
            navigation.classList.remove('is-compact');
            const available = inner.clientWidth;
            inner.style.display = 'flex';
            inner.style.width = 'max-content';
            const needed = inner.scrollWidth;
            inner.style.removeProperty('display');
            inner.style.removeProperty('width');
            compact = needed > available;
        }

        navigation.classList.toggle('is-compact', compact);
        getComputedStyle(navigation).getPropertyValue('--logo-height');
        navigation.style.removeProperty('transition');
    }

    // The menu covers the header, which goes inert under it; its own close
    // button (or Esc) shuts it and focus goes back to the button that opened it
    if (dialog) {
        let opener;
        // Where the page was: moving focus into the menu and back to the header
        // makes the browser scroll them clear of scroll-padding-top
        let pageY = 0;

        function keepPage() {
            if (window.scrollY !== pageY) window.scrollTo({top: pageY, behavior: 'instant'});
        }

        function openMenu(burger) {
            document.dispatchEvent(new Event('psy:close-search'));
            opener = burger;
            pageY = window.scrollY;
            dialog.show();
            psyPageInert(true);
            navigation.inert = true;
            keepPage();
            burgers.forEach(b => psySetExpanded(b, true));
            navigation.classList.add('is-menu-open');
        }

        burgers.forEach(function (burger) {
            burger.addEventListener('click', function () {
                if (dialog.open) {
                    dialog.close();
                } else {
                    openMenu(burger);
                }
            });
        });

        dialog.querySelector('.psy-menu-close')?.addEventListener('click', function () {
            dialog.close();
        });

        dialog.addEventListener('close', function () {
            // 'close' fires a moment later: if the menu's search button shut it,
            // the search bar is open by now and keeps the page inert
            if (document.getElementById('psy-search')?.hidden !== false) psyPageInert(false);
            navigation.inert = false;
            burgers.forEach(b => psySetExpanded(b, false));
            navigation.classList.remove('is-menu-open');
            // Back to the button that opened it, if focus was left in the menu
            if (opener && (dialog.contains(document.activeElement) || document.activeElement === document.body)) {
                opener.focus({preventScroll: true});
            }
            keepPage();
        });

        // A non-modal dialog doesn't close on Esc by itself
        document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape' && dialog.open) dialog.close();
        });

        // Portal opens its own popup, so get out of its way (its own
        // handlers still run)
        dialog.addEventListener('click', function (event) {
            if (event.target.closest('[data-portal], a[href^="#"]')) {
                dialog.close();
            }
        });

    }

    let frame;
    window.addEventListener('resize', function () {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(fitMenu);
    });

    fitMenu();
    // Web fonts and the logo change the widths once they load
    document.fonts?.ready.then(fitMenu);
    window.addEventListener('load', fitMenu);
})();

/* Search: the search buttons (data-psy-search) drop a search bar down under
   the header, like the site menu, instead of opening Ghost's search popup.
   Results come as you type from the Content API (the key is the one Ghost
   gives its own search script): posts whose title or excerpt contains the
   words, and tags and authors whose name does */
(function () {
    const panel = document.getElementById('psy-search');
    const navigation = document.querySelector('.gh-navigation');
    if (!panel || !navigation) return;

    const input = panel.querySelector('.psy-search-input');
    const results = panel.querySelector('.psy-search-results');
    // Screen readers hear just the count, not the whole list as it changes
    const status = panel.querySelector('.psy-search-status');
    const toggles = document.querySelectorAll('[data-psy-search]');
    // Ghost's own search script isn't loaded (default.hbs), so the Content API
    // key and address come from Portal's script tag; sodo-search's if it's back
    const portal = document.querySelector('script[data-ghost][data-key][data-api]');
    const ghostSearch = document.querySelector('script[data-sodo-search]');
    const key = portal ? portal.dataset.key : ghostSearch && ghostSearch.dataset.key;
    const api = portal && portal.dataset.api
        ? portal.dataset.api.replace(/\/?$/, '/')
        : (ghostSearch && ghostSearch.dataset.sodoSearch || '/').replace(/\/?$/, '/') + 'ghost/api/content/';

    // The words come from the template (data-text-*, translated there), with
    // {count} / {total} / {query} filled in; English if an attribute is missing
    function text(name, fallback, values) {
        let words = panel.dataset[name] || fallback;
        // split/join, not replace: a query with $& or $1 in it stays literal
        Object.entries(values || {}).forEach(([key, value]) => words = words.split('{' + key + '}').join(value));
        return words;
    }

    let opener;
    let pageY = 0;
    let timer;
    let request;

    function keepPage() {
        if (window.scrollY !== pageY) window.scrollTo({top: pageY, behavior: 'instant'});
    }

    function place() {
        panel.style.setProperty('--search-top', navigation.getBoundingClientRect().bottom + 'px');
    }

    function setExpanded(open) {
        document.querySelectorAll('.gh-search[data-psy-search]').forEach(t => psySetExpanded(t, open));
    }

    function open(from) {
        const menu = document.getElementById('psy-menu');
        if (menu && menu.open) menu.close();
        // The menu's search button is gone once the menu shuts, so focus
        // returns to the header's
        opener = menu && menu.contains(from)
            ? [...navigation.querySelectorAll('.gh-search[data-psy-search]')].find(t => t.offsetParent) || from
            : from;
        pageY = window.scrollY;
        place();
        panel.hidden = false;
        psyPageInert(true);
        setExpanded(true);
        input.focus({preventScroll: true});
        input.select();
        keepPage();
    }

    function close() {
        if (panel.hidden) return;
        panel.hidden = true;
        psyPageInert(false);
        setExpanded(false);
        if (opener && (panel.contains(document.activeElement) || document.activeElement === document.body)) {
            opener.focus({preventScroll: true});
        }
        keepPage();
    }

    toggles.forEach(function (toggle) {
        toggle.addEventListener('click', function (event) {
            event.preventDefault();
            if (!panel.hidden && toggle.matches('.gh-search')) {
                close();
            } else {
                open(toggle);
            }
        });
    });

    document.addEventListener('psy:close-search', close);
    panel.querySelector('.psy-search-close').addEventListener('click', close);

    document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !panel.hidden) close();
    });

    // A click outside the bar and its results (and not on a toggle) closes it
    document.addEventListener('click', function (event) {
        if (panel.hidden || panel.contains(event.target) || event.target.closest('[data-psy-search]')) return;
        close();
    });

    window.addEventListener('resize', function () {
        if (!panel.hidden) requestAnimationFrame(place);
    });
    navigation.addEventListener('transitionend', function () {
        if (!panel.hidden) place();
    });

    // Enter goes to the first result
    panel.querySelector('form').addEventListener('submit', function (event) {
        event.preventDefault();
        const first = results.querySelector('a');
        if (first) window.location.href = first.href;
    });

    function el(tag, className, text) {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text) node.textContent = text;
        return node;
    }

    const dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {month: 'long', day: 'numeric', year: 'numeric'});

    function show(query, data) {
        results.replaceChildren();
        const [posts, tags, authors] = data.map(found => found.items);

        // Each list stops at its limit, so the count shown can be less than
        // the number that match; say both then
        const count = posts.length + tags.length + authors.length;
        const total = data.reduce((sum, found) => sum + found.total, 0);
        if (status) {
            status.textContent = !count ? text('textNoResults', 'No results')
                : total > count ? text('textResultsShown', '{total} results, showing {count}', {total: total, count: count})
                : count === 1 ? text('textOneResult', '1 result')
                : text('textResults', '{count} results', {count: count});
        }

        if (!count) {
            results.append(el('p', 'psy-search-empty', text('textNoResultsFor', 'No results for “{query}”', {query: query})));
            return;
        }

        const inner = el('div', 'psy-search-inner');

        [[text('textTags', 'Tags'), tags], [text('textAuthors', 'Authors'), authors]].forEach(function ([heading, items]) {
            if (!items.length) return;
            const group = el('div', 'psy-search-chips');
            group.append(el('h2', 'psy-search-heading', heading));
            const list = el('ul');
            items.forEach(function (item) {
                const li = el('li');
                const a = el('a', null, item.name);
                a.href = item.url;
                li.append(a);
                list.append(li);
            });
            group.append(list);
            inner.append(group);
        });

        if (posts.length) {
            const group = el('div', 'psy-search-posts');
            group.append(el('h2', 'psy-search-heading', text('textPosts', 'Posts')));
            const list = el('ul');
            posts.forEach(function (post) {
                const li = el('li');
                const a = el('a', 'psy-search-post');
                a.href = post.url;
                a.append(el('span', 'psy-search-post-title', post.title));
                const meta = [post.published_at ? dateFormat.format(new Date(post.published_at)) : '', post.custom_excerpt || post.excerpt || ''].filter(Boolean);
                if (post.published_at) a.append(el('span', 'psy-search-post-date', meta.shift()));
                if (meta.length) a.append(el('span', 'psy-search-post-excerpt', meta[0]));
                li.append(a);
                list.append(li);
            });
            group.append(list);
            if (data[0].total > posts.length) {
                group.append(el('p', 'psy-search-post-date', text('textPostsShown', 'Showing the {count} newest of {total} posts. Type more to narrow the list.', {count: posts.length, total: data[0].total})));
            }
            inner.append(group);
        }

        results.append(inner);
    }

    // The items and how many match in all (more than the items past the limit)
    function get(resource, params, signal) {
        const url = new URL(api + resource + '/');
        url.searchParams.set('key', key);
        Object.entries(params).forEach(([name, value]) => url.searchParams.set(name, value));
        return fetch(url, {signal}).then(function (response) {
            if (!response.ok) throw new Error(response.status);
            return response.json();
        }).then(function (json) {
            const items = json[resource];
            const pagination = json.meta && json.meta.pagination;
            return {items, total: pagination ? pagination.total : items.length};
        });
    }

    function fail() {
        const message = text('textError', 'Search isn’t available right now. Please try again.');
        results.replaceChildren(el('p', 'psy-search-empty', message));
        results.hidden = false;
        if (status) status.textContent = message;
    }

    // An NQL filter matching any of the fields against the query. NQL strings
    // only escape quotes: a backslash isn't an escape there (and one at the
    // end breaks the filter), so backslashes go. Titles mix curly and straight
    // apostrophes, so either one typed matches both
    function filter(fields, query) {
        const words = query.replace(/\\/g, '');
        const variants = [...new Set([words, words.replace(/'/g, '’'), words.replace(/’/g, "'")])];
        return variants.flatMap(function (variant) {
            const q = "'" + variant.replace(/['"]/g, '\\$&') + "'";
            return fields.map(field => field + ':~' + q);
        }).join(',');
    }

    function search() {
        // Long enough for any title; far longer only makes the request URLs
        // too long for the server
        const query = input.value.trim().slice(0, 100);
        if (request) request.abort();

        if (query.replace(/\\/g, '').length < 2) {
            results.hidden = true;
            results.replaceChildren();
            if (status) status.textContent = '';
            return;
        }

        // No Content API key (Ghost's search script missing): say so rather
        // than leave the field doing nothing
        if (!key) {
            fail();
            return;
        }

        request = new AbortController();
        const signal = request.signal;

        Promise.all([
            get('posts', {filter: filter(['title', 'custom_excerpt'], query), fields: 'title,url,published_at,custom_excerpt', order: 'published_at desc', limit: 10}, signal),
            get('tags', {filter: 'visibility:public+(' + filter(['name'], query) + ')', fields: 'name,url', limit: 6}, signal),
            get('authors', {filter: filter(['name'], query), fields: 'name,url', limit: 4}, signal)
        ]).then(function (data) {
            show(query, data);
            results.hidden = false;
        }).catch(function (error) {
            if (error.name === 'AbortError') return;
            fail();
        });
    }

    input.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(search, 200);
    });
})();

/* Series pages are titled "Series: Part", so menu cards and the Series page
   cards name just the series */
function psyShortTitles(root) {
    root.querySelectorAll('.has-short-title .psy-menu-card-title, .is-series-name').forEach(function (title) {
        title.textContent = title.textContent.split(': ')[0];
    });
}
psyShortTitles(document);

/* The menu's Highlights cards live at /menu-highlights/ (menu-highlights.hbs), so
   pages don't run their queries on every view. Fetched once, as soon as a reader
   points at, touches or tabs to a menu button, or failing that when the menu opens */
(function () {
    const slot = document.querySelector('#psy-menu [data-psy-highlights]');
    if (!slot) return;
    const buttons = document.querySelectorAll('.gh-burger, .gh-menu-button');
    let loading = null;

    function load() {
        if (loading) return loading;
        loading = fetch('/menu-highlights/', {credentials: 'same-origin'})
            .then(function (response) {
                if (!response.ok) throw new Error(response.status);
                return response.text();
            })
            .then(function (html) {
                const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-psy-highlights]');
                if (!list) return;
                slot.replaceChildren(...Array.from(list.children).map(node => document.importNode(node, true)));
                psyShortTitles(slot);
                slot.classList.add('is-loaded');
            })
            .catch(function () {
                // Offline or blocked: try again next time
                loading = null;
            });
        return loading;
    }

    buttons.forEach(function (button) {
        ['pointerenter', 'pointerdown', 'focus', 'click'].forEach(function (type) {
            button.addEventListener(type, load, {passive: true});
        });
    });
})();

/* Shrink the sticky header once the page is scrolled */
(function () {
    const navigation = document.querySelector('.gh-navigation');
    if (!navigation) return;

    let shrunk = false;

    // Separate on/off thresholds so the header's own height change can't flip it back
    function update() {
        // The open mobile menu pins the page at scrollY 0; keep the size it opened with
        if (document.documentElement.classList.contains('gh-navigation-open')) return;

        const next = shrunk ? window.scrollY > 20 : window.scrollY > 100;
        if (next === shrunk) return;

        shrunk = next;
        navigation.classList.toggle('is-shrunk', shrunk);
    }

    update();
    window.addEventListener('scroll', update, {passive: true});
})();

/* Hide the "Powered by Ghost" badge in Portal, which renders in a same-origin iframe the theme CSS can't reach */
(function () {
    const css = '.gh-portal-powered { display: none !important; }';

    function patch(frame) {
        const doc = frame.contentDocument;
        if (!doc || !doc.head || doc.getElementById('hide-portal-powered')) return;

        const style = doc.createElement('style');
        style.id = 'hide-portal-powered';
        style.textContent = css;
        doc.head.appendChild(style);
    }

    new MutationObserver(function () {
        document.querySelectorAll('#ghost-portal-root iframe').forEach(function (frame) {
            patch(frame);
            if (!frame.dataset.poweredPatch) {
                frame.dataset.poweredPatch = 'true';
                frame.addEventListener('load', function () {
                    patch(frame);
                });
            }
        });
    }).observe(document.body, {childList: true, subtree: true});
})();

/* Signup forms marked data-portal-signup-form (the "Become a member" section above
   the footer) open Portal's signup page, where the plans are, with the email already
   typed in. Portal can't take an email in its link, so once its popup (a same-origin
   iframe) shows the signup form, the email field is filled the way typing would
   (React only notices the native setter plus an input event) */
(function () {
    const forms = document.querySelectorAll('[data-portal-signup-form]');
    if (!forms.length) return;

    // Portal may only start loading now, so the 8 s count starts once it's ready
    async function fillPortalEmail(email) {
        await psyLoadPortal();
        const started = Date.now();
        const timer = setInterval(function () {
            if (Date.now() - started > 8000) return clearInterval(timer);
            for (const frame of document.querySelectorAll('#ghost-portal-root iframe')) {
                const doc = frame.contentDocument;
                const input = doc && doc.querySelector('input[type="email"]');
                if (!input) continue;
                clearInterval(timer);
                const view = frame.contentWindow;
                const setValue = Object.getOwnPropertyDescriptor(view.HTMLInputElement.prototype, 'value').set;
                setValue.call(input, email);
                input.dispatchEvent(new view.Event('input', {bubbles: true}));
                // Portal asks for a name first when it has that field; otherwise the button is next
                const name = doc.querySelector('input[name="name"], input#input-name');
                (name && !name.value ? name : input).focus();
                return;
            }
        }, 100);
    }

    forms.forEach(function (form) {
        form.addEventListener('submit', function (event) {
            event.preventDefault();
            const email = form.querySelector('input[type="email"]').value.trim();
            window.location.hash = '#/portal/signup';
            if (email) fillPortalEmail(email);
        });
    });
})();

/* Donate page: the donate service sends the reader back from Stripe Checkout with
   ?donation=thanks, or with why the recurring donation didn't go through. Say so
   under the section's heading (#give), and on a problem reopen the form */
(function () {
    const status = new URLSearchParams(window.location.search).get('donation');
    const section = document.getElementById('give');
    if (!status || !section) return;

    const messages = {
        thanks: 'Thank you! Your recurring donation is set up. We\'ve emailed you a link to your Psymposia account, where you can update your card or cancel anytime.',
        amount: 'Please enter an amount from $1 to $10,000.',
        busy: 'Too many tries in a short time. Please wait a few minutes and try again.',
        error: 'We couldn\'t start the payment. Please try again, or email gifts@psymposia.com.'
    };
    const notice = document.createElement('p');
    notice.className = 'psy-donate-notice';
    notice.setAttribute('role', status === 'thanks' ? 'status' : 'alert');
    notice.textContent = messages[status] || messages.error;
    const title = section.querySelector('.psy-donate-title');
    if (title) {
        title.after(notice);
    } else {
        section.prepend(notice);
    }

    const form = document.getElementById('psy-give');
    if (status !== 'thanks' && form && form.showPopover) {
        form.showPopover();
    }

    // A reload shouldn't say it again
    history.replaceState(null, '', window.location.pathname + window.location.hash);
})();

/* Dark mode toggle (sun/moon in the header). default.hbs defines psymposiaSetScheme
   and applies the saved or system scheme before paint; a click saves the choice,
   and without a saved choice the page follows system changes live */
(function () {
    const root = document.documentElement;
    const buttons = document.querySelectorAll('[data-theme-toggle]');
    const system = window.matchMedia('(prefers-color-scheme: dark)');
    if (!buttons.length || !window.psymposiaSetScheme) return;

    const apply = function (dark) {
        window.psymposiaSetScheme(dark);
        buttons.forEach(function (button) {
            button.setAttribute('aria-pressed', dark ? 'true' : 'false');
        });
    };

    // Ghost's comments read the page's colors once, when they load, so remount
    // them (fresh copy of their script, scheme set explicitly) after a switch
    const remountComments = function (dark) {
        const script = document.querySelector('script[data-ghost-comments]');
        const mounted = document.getElementById('ghost-comments-root');
        if (!script || !mounted) return;
        const fresh = document.createElement('script');
        Array.from(script.attributes).forEach(function (attribute) {
            fresh.setAttribute(attribute.name, attribute.value);
        });
        fresh.setAttribute('data-color-scheme', dark ? 'dark' : 'light');
        mounted.remove();
        script.replaceWith(fresh);
    };

    const change = function (dark) {
        apply(dark);
        remountComments(dark);
    };

    const saved = function () {
        try {
            return localStorage.getItem('psymposia-scheme');
        } catch (e) {
            return null;
        }
    };

    apply(root.classList.contains('is-dark'));

    buttons.forEach(function (button) {
        button.addEventListener('click', function () {
            const dark = !root.classList.contains('is-dark');
            change(dark);
            try {
                localStorage.setItem('psymposia-scheme', dark ? 'dark' : 'light');
            } catch (e) {
                // Private mode etc.: the choice just lasts until the next page
            }
        });
    });

    system.addEventListener('change', function (event) {
        if (!saved()) change(event.matches);
    });
})();

/* Add lightbox to gallery and feature images */
(function () {
    lightbox(
        '.kg-image-card:not(.team-member-photo) > .kg-image[width][height], .kg-gallery-image > img, .gh-feature-image'
    );
})();

/* Responsive video in post content */
(function () {
    const sources = [
        '.gh-content iframe[src*="youtube.com"]',
        '.gh-content iframe[src*="youtube-nocookie.com"]',
        '.gh-content iframe[src*="player.vimeo.com"]',
        '.gh-content iframe[src*="kickstarter.com"][src*="video.html"]',
        // Dailymotion's oEmbed sizes its player 840px wide (wider than a phone)
        '.gh-content iframe[src*="dailymotion.com"]',
        '.gh-content object',
        '.gh-content embed',
    ];
    reframe(document.querySelectorAll(sources.join(',')));
})();

/* Podcast embeds (Libsyn, Spotify) are heavy: each pulls a third-party player
   with its own scripts before the reader has scrolled near it, and none arrive
   with loading="lazy" from the WordPress import. Held back until they scroll
   into view instead (same pattern as the video facade). Their fixed heights
   (Libsyn 90px, Spotify 152px) mean nothing moves when they load; the swap is
   src→data-src, the iframe itself never leaves the document. Runs before the
   facade so a podcast block above a video doesn't claim the reader's first
   scroll tick */
(function () {
    const embeds = document.querySelectorAll(
        '.gh-content iframe[src*="libsyn.com"], .gh-content iframe[src*="open.spotify.com"]'
    );
    if (!embeds.length) return;
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            const iframe = entry.target;
            observer.unobserve(iframe);
            iframe.src = iframe.dataset.src;
        });
    }, {rootMargin: '200px'});

    embeds.forEach(function (iframe) {
        iframe.dataset.src = iframe.src;
        iframe.removeAttribute('src');
        observer.observe(iframe);
    });
})();

/* Video facade (.psy-video, screen.css "Embed"): a click on the thumbnail swaps
   in YouTube's player, playing, and moves focus to it. Ctrl/⌘/Shift-clicks and
   middle clicks keep the link's own behaviour (the video on YouTube).
   Timestamp links in the post (a transcript's "12:34", to youtube.com/watch?v=ID&t=…
   or youtu.be/ID?t=…) play this page's player of the same video from that point
   instead of leaving for YouTube; links to other videos are left alone */
(function () {
    const players = document.querySelectorAll('.psy-video[data-youtube]');
    if (!players.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // "754", "754s", "12m34s", "1h2m3s" → seconds
    function seconds(value) {
        if (!value) return 0;
        if (/^\d+s?$/.test(value)) return parseInt(value, 10);
        const parts = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
        if (!parts) return 0;
        return (parseInt(parts[1] || 0, 10) * 3600) + (parseInt(parts[2] || 0, 10) * 60) + parseInt(parts[3] || 0, 10);
    }

    function play(player, start) {
        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(player.dataset.youtube) +
            '?autoplay=1' + (start ? '&start=' + start : '') + '&rel=0';
        iframe.title = player.dataset.title || 'YouTube video player';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allowFullscreen = true;
        player.replaceChildren(iframe);
        iframe.focus({preventScroll: true});
    }

    function plainClick(event) {
        return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    players.forEach(function (player) {
        const link = player.querySelector('.psy-video-link');
        if (!link) return;
        link.addEventListener('click', function (event) {
            if (!plainClick(event)) return;
            event.preventDefault();
            play(player, seconds(player.dataset.start));
        });
        // A link answers Enter by itself (as a click); Space is what a play button takes
        link.addEventListener('keydown', function (event) {
            if (event.key !== ' ') return;
            event.preventDefault();
            play(player, seconds(player.dataset.start));
        });
    });

    // The video and start time a YouTube watch link points to (null if it has no time)
    function timestamp(href) {
        let url;
        try {
            url = new URL(href, location.href);
        } catch (e) {
            return null;
        }
        const host = url.hostname.replace(/^(www|m)\./, '');
        let id = null;
        if (host === 'youtube.com' && url.pathname === '/watch') id = url.searchParams.get('v');
        if (host === 'youtu.be') id = url.pathname.split('/')[1];
        if (!id) return null;
        const t = url.searchParams.get('t') || url.searchParams.get('start') || (url.hash.match(/t=([0-9hms]+)/) || [])[1];
        return t ? {id: id, start: seconds(t)} : null;
    }

    document.querySelectorAll('.gh-content a[href*="youtube.com/watch"], .gh-content a[href*="youtu.be/"]').forEach(function (link) {
        if (link.closest('.psy-video')) return;
        const to = timestamp(link.href);
        if (!to) return;
        const player = Array.prototype.find.call(players, function (item) {
            return item.dataset.youtube === to.id;
        });
        if (!player) return;
        link.addEventListener('click', function (event) {
            if (!plainClick(event)) return;
            event.preventDefault();
            // Already playing: a fresh player from the new point (seeking would need YouTube's API script)
            play(player, to.start);
            player.scrollIntoView({block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth'});
        });
    });
})();

/* GIFs turned into looping videos (video[data-gif-video], autoplaying and muted)
   hold still for readers whose system asks for reduced motion: paused, with
   controls to play them */
(function () {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function hold() {
        if (!reduceMotion.matches) return;
        document.querySelectorAll('video[data-gif-video]').forEach(function (video) {
            video.removeAttribute('autoplay');
            video.autoplay = false;
            video.pause();
            video.controls = true;
        });
    }

    hold();
    reduceMotion.addEventListener('change', hold);
})();

/* Infinite scroll pagination */
(function () {
    if (!document.body.classList.contains('home-template') && !document.body.classList.contains('post-template')) {
        pagination();
    }
})();

/* Responsive HTML table */
(function () {
    const tables = document.querySelectorAll('.gh-content > table:not(.gist table)');
    
    tables.forEach(function (table) {
        const wrapper = document.createElement('div');
        wrapper.className = 'gh-table';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    });
})();

/* Subscribe bar (components/subscribe-bar): up from page load, and out of
   the way while another signup form is on screen, from the footer's "Become a member" section down, and while the
   site menu or search is open. It stays while its field has focus. ✕ hides it
   for 30 days (this browser only); a signup or sign-in from it hides it for
   the rest of the page view. Not on /donate/, which is a signup page itself.
   It never comes up on screens 500px tall or less
   (phones held sideways, zoomed-in text), where it would cover a third of
   the page. While it's up, --bar-h on <html> keeps the element tabbed to
   above it (scroll-padding-bottom, screen.css 23) */
(function () {
    const bar = document.querySelector('.psy-subscribe-bar');
    if (!bar) return;
    if (document.body.classList.contains('page-donate')) {
        bar.remove();
        return;
    }

    const storageKey = 'psymposia-subscribe-bar-closed';
    const closedFor = 30 * 24 * 60 * 60 * 1000;
    try {
        const closed = Number(localStorage.getItem(storageKey));
        if (closed && Date.now() - closed < closedFor) {
            bar.remove();
            return;
        }
    } catch (e) {
        // No storage (private mode, blocked): the bar just can't remember ✕
    }

    const footerSignup = document.querySelector('.gh-footer-signup');
    const menu = document.getElementById('psy-menu');
    const search = document.getElementById('psy-search');
    const short = window.matchMedia('(max-height: 500px)');
    const formsOnScreen = new Set();
    let done = false;
    let frame;

    function update() {
        frame = null;
        const pastFooter = footerSignup && footerSignup.getBoundingClientRect().top < window.innerHeight;
        const covered = (menu && menu.open) || (search && !search.hidden);
        const show = !done && !short.matches && !formsOnScreen.size && !pastFooter && !covered;
        if (!show && !done && bar.contains(document.activeElement)) return;
        bar.hidden = !show;
        if (show) document.documentElement.style.setProperty('--bar-h', bar.offsetHeight + 'px');
        else document.documentElement.style.removeProperty('--bar-h');
    }

    function schedule() {
        if (!frame) frame = requestAnimationFrame(update);
    }

    const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (entry.isIntersecting) formsOnScreen.add(entry.target);
            else formsOnScreen.delete(entry.target);
        });
        schedule();
    });
    document.querySelectorAll('.gh-form').forEach(function (form) {
        if (!bar.contains(form)) observer.observe(form);
    });

    // The menu (a dialog) and search (a [hidden] panel) opening and closing
    const watcher = new MutationObserver(schedule);
    if (menu) watcher.observe(menu, {attributes: true, attributeFilter: ['open']});
    if (search) watcher.observe(search, {attributes: true, attributeFilter: ['hidden']});

    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);

    function finish() {
        done = true;
        bar.hidden = true;
        document.documentElement.style.removeProperty('--bar-h');
    }

    bar.querySelector('.psy-subscribe-bar-close').addEventListener('click', function () {
        try {
            localStorage.setItem(storageKey, String(Date.now()));
        } catch (e) {
            // Hidden for this page view only
        }
        finish();
    });
    bar.querySelector('form').addEventListener('submit', finish);
    bar.querySelector('.psy-subscribe-bar-signin').addEventListener('click', finish);

    schedule();
})();

/* "becoming a member" in the box above the footer opens Portal's signup, with its
   plans. The text is a plain-text Design setting ("Become a member text"), so the
   link goes in here; without the phrase the text stays as it is */
(function () {
    const text = document.querySelector('.gh-footer-signup-text');
    const node = text && text.firstChild;
    if (!node || node.nodeType !== Node.TEXT_NODE) return;
    const match = /becoming a member/i.exec(node.data);
    if (!match) return;
    const phrase = node.splitText(match.index);
    phrase.splitText(match[0].length);
    const link = document.createElement('a');
    link.href = '#/portal/signup';
    link.dataset.portal = 'signup';
    phrase.replaceWith(link);
    link.append(phrase);
})();
